import type { AxiosError } from "axios";
import type { UseFormSetError } from "react-hook-form";
import type { InfractionCreateSchema } from "../schemas/infraction-create.schema";

type InfractionApiErrorBody = {
	error?: {
		fieldsErrors?: Record<string, string[]>;
		errors?: string[];
		detail?: string;
	};
	errors?: Record<string, string[]>;
	fieldsErrors?: Record<string, string[]>;
};

const INFRACTION_FORM_FIELDS = new Set<keyof InfractionCreateSchema>([
	"vehicleId",
	"ait",
	"issuingAuthorityId",
	"issuingAuthorityCode",
	"isAnttInfraction",
	"occurredDate",
	"occurredTime",
	"location",
	"infractionCode",
	"breakdown",
	"description",
	"severity",
	"points",
	"offenderType",
	"originalAmount",
	"driverIndicationDeadline",
	"dueDate",
	"paymentDate",
	"driverId",
	"transferPointsToDriver",
	"infractionNoticeFile",
	"penaltyNotificationFile",
	"receiptsFile",
]);

const API_FIELD_TO_FORM_FIELD: Partial<
	Record<string, keyof InfractionCreateSchema>
> = {
	AIT: "ait",
	Attachments: "infractionNoticeFile",
};

function pascalToCamel(value: string): string {
	if (!value) {
		return value;
	}

	return value.charAt(0).toLowerCase() + value.slice(1);
}

function resolveFormFieldFromApiField(
	apiField: string
): keyof InfractionCreateSchema | null {
	if (apiField === "Status") {
		return null;
	}

	const mappedField = API_FIELD_TO_FORM_FIELD[apiField];

	if (mappedField) {
		return mappedField;
	}

	const formField = pascalToCamel(apiField) as keyof InfractionCreateSchema;

	if (INFRACTION_FORM_FIELDS.has(formField)) {
		return formField;
	}

	return null;
}

export function extractInfractionApiFieldsErrors(
	error: unknown
): Record<string, string[]> | null {
	if (!error || typeof error !== "object") {
		return null;
	}

	const axiosError = error as AxiosError<InfractionApiErrorBody>;
	const responseData = axiosError.response?.data;

	const nestedFieldsErrors = responseData?.error?.fieldsErrors;

	if (nestedFieldsErrors && typeof nestedFieldsErrors === "object") {
		return nestedFieldsErrors;
	}

	const aspNetErrors = responseData?.errors;

	if (aspNetErrors && typeof aspNetErrors === "object") {
		return aspNetErrors;
	}

	const directError = error as {
		fieldsErrors?: Record<string, string[]>;
		response?: { data?: { fieldsErrors?: Record<string, string[]> } };
	};

	if (
		directError.fieldsErrors &&
		typeof directError.fieldsErrors === "object"
	) {
		return directError.fieldsErrors;
	}

	const responseFieldsErrors = directError.response?.data?.fieldsErrors;

	if (responseFieldsErrors && typeof responseFieldsErrors === "object") {
		return responseFieldsErrors;
	}

	return null;
}

export function applyInfractionApiFieldErrors(
	error: unknown,
	setError: UseFormSetError<InfractionCreateSchema>,
	onStatusError: (message: string | null) => void
): boolean {
	const fieldsErrors = extractInfractionApiFieldsErrors(error);

	if (!fieldsErrors) {
		return false;
	}

	let hasMappedError = false;

	for (const [apiField, messages] of Object.entries(fieldsErrors)) {
		const message = messages[0];

		if (!message) {
			continue;
		}

		if (apiField === "Status") {
			onStatusError(message);
			hasMappedError = true;
			continue;
		}

		const formField = resolveFormFieldFromApiField(apiField);

		if (!formField) {
			continue;
		}

		setError(formField, {
			type: "server",
			message,
		});
		hasMappedError = true;
	}

	return hasMappedError;
}
